/**
 * BUG-060 — persistent header pill that surfaces the active AI
 * figure-generation job. Renders when a job is running OR completed
 * with at least one candidate still pickable. Clicking the pill
 * re-opens the GenerateAiModal on the matching step; clicking the
 * X close button dismisses the pill for the rest of the session
 * (next page load re-shows it iff the job still has unconsumed
 * candidates).
 *
 * Auto-hide rules:
 *   - status === 'idle' or no active job_id → hide (initial state).
 *   - status === 'completed' AND every candidate consumed → hide
 *     (the toast has nothing left to surface; A2's multi-pick
 *     left the row durable in the Recent Generations panel
 *     instead).
 *   - dismissedGenerationJobIds includes the active job_id → hide
 *     (drafter clicked the X on this session).
 *
 * Polling is owned by the store (drawingStore._startGenerationJobPoll);
 * this component is a pure projection of that state.
 */
import React, { useCallback } from 'react';
import { Loader2, CheckCircle2, AlertTriangle, X } from 'lucide-react';
import { useDrawingStore } from '@/store/drawingStore';

// ``left-1/2 -translate-x-1/2`` centres the pill horizontally at
// the bottom of the viewport so it doesn't overlap either rail
// (the FigureGallery sidebar on the left, the ReferencePanel on
// the right). ``bottom-4`` keeps it above the WorkbenchStatusStrip
// when that strip surfaces (offline / pending mutations).
const baseCls =
  'fixed bottom-4 left-1/2 -translate-x-1/2 z-30 inline-flex items-center gap-2 rounded-md '
  + 'px-3 py-2 text-xs font-medium shadow-md cursor-pointer '
  + 'transition-colors';


function _availableCount(state: {
  candidates?: { temp_id?: string }[] | null;
  consumed_candidate_temp_ids?: string[] | null;
} | null): number {
  if (!state) return 0;
  const total = Array.isArray(state.candidates) ? state.candidates.length : 0;
  const consumed = Array.isArray(state.consumed_candidate_temp_ids)
    ? state.consumed_candidate_temp_ids.length
    : 0;
  return Math.max(total - consumed, 0);
}


export default function GenerationJobPill(): JSX.Element | null {
  const status = useDrawingStore((s) => s.generationJobStatus);
  const jobId = useDrawingStore((s) => s.activeGenerationJobId);
  const jobState = useDrawingStore((s) => s.generationJobState);
  const dismissed = useDrawingStore((s) => s.dismissedGenerationJobIds);
  const openGenerationModal = useDrawingStore(
    (s) => s.openGenerationModal,
  );
  const dismissPill = useDrawingStore(
    (s) => s.dismissGenerationJobPill,
  );

  const onDismiss = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (jobId) dismissPill(jobId);
    },
    [jobId, dismissPill],
  );

  if (!jobId || status === 'idle') return null;
  if (dismissed.has(jobId)) return null;

  if (status === 'queued' || status === 'running') {
    return (
      <div
        className={`${baseCls} bg-zinc-900 text-white hover:bg-zinc-800`}
        data-testid="generation-job-pill"
        data-status={status}
      >
        <button
          type="button"
          className="inline-flex items-center gap-2"
          onClick={openGenerationModal}
          title="A figure-generation job is running in the background"
        >
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          <span>Generating figures…</span>
          <span className="ml-1 rounded-sm bg-white/15 px-1.5 py-0.5 text-[10px]">
            View progress
          </span>
        </button>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss generation pill"
          className="ml-1 rounded-sm p-0.5 text-white/60 hover:text-white hover:bg-white/10"
          data-testid="generation-job-pill-dismiss"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    );
  }

  if (status === 'completed') {
    // Auto-hide once every candidate has been promoted into a
    // drawing_figures row. The Recent Generations panel keeps a
    // durable surface for the row regardless; the pill's job is
    // to nudge "you have unselected candidates" specifically.
    const available = _availableCount(jobState);
    if (available <= 0) return null;
    const total = jobState?.candidates?.length ?? 0;
    const summary =
      total > 0 && available !== total
        ? `${available} of ${total} candidates ready`
        : 'Figures ready';
    return (
      <div
        className={`${baseCls} bg-emerald-600 text-white hover:bg-emerald-700`}
        data-testid="generation-job-pill"
        data-status="completed"
      >
        <button
          type="button"
          className="inline-flex items-center gap-2"
          onClick={openGenerationModal}
          title="Figure candidates ready — click to select"
        >
          <CheckCircle2 className="h-3.5 w-3.5" />
          <span>✓ {summary}</span>
          <span className="ml-1 rounded-sm bg-white/15 px-1.5 py-0.5 text-[10px]">
            Select
          </span>
        </button>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss generation pill"
          className="ml-1 rounded-sm p-0.5 text-white/70 hover:text-white hover:bg-white/15"
          data-testid="generation-job-pill-dismiss"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    );
  }

  if (status === 'failed' || status === 'cancelled') {
    const isFailed = status === 'failed';
    return (
      <div
        className={`${baseCls} bg-rose-600 text-white hover:bg-rose-700`}
        data-testid="generation-job-pill"
        data-status={status}
      >
        <button
          type="button"
          className="inline-flex items-center gap-2"
          onClick={openGenerationModal}
          title={
            isFailed
              ? 'Generation failed — click to view + retry'
              : 'Generation cancelled — click to view + retry'
          }
        >
          <AlertTriangle className="h-3.5 w-3.5" />
          <span>{isFailed ? '⚠ Generation failed' : 'Generation cancelled'}</span>
          <span className="ml-1 rounded-sm bg-white/15 px-1.5 py-0.5 text-[10px]">
            Retry
          </span>
        </button>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss generation pill"
          className="ml-1 rounded-sm p-0.5 text-white/70 hover:text-white hover:bg-white/15"
          data-testid="generation-job-pill-dismiss"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    );
  }

  return null;
}
